import { useEffect, useRef, useState } from "react";
import paper from "paper";

interface UseCanvasSetupReturn {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  canvasSize: { width: number; height: number };
  paperInitializedRef: React.MutableRefObject<boolean>;
}

/**
 * Hook to handle canvas setup including resize observer and Paper.js initialization.
 * Device pixel ratio scaling for crisp rendering on high-DPR displays (mobile Retina,
 * etc.) is handled entirely by Paper.js's own CanvasView — see the comment below.
 */
export const useCanvasSetup = (): UseCanvasSetupReturn => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const paperInitializedRef = useRef(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = (entries?: ResizeObserverEntry[]) => {
      if (entries && entries.length > 0) {
        const { width, height } = entries[0].contentRect;
        setCanvasSize({ width, height });
      } else {
        const rect = container.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };

    const rafId = requestAnimationFrame(() => updateSize());
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!canvasRef.current || canvasSize.width === 0 || canvasSize.height === 0) return;

    const canvas = canvasRef.current;
    canvas.style.width = `${canvasSize.width}px`;
    canvas.style.height = `${canvasSize.height}px`;

    if (!paperInitializedRef.current) {
      paper.setup(canvas);
      paperInitializedRef.current = true;
    }

    // Always update viewSize to match CSS dimensions (without resetting zoom/pan)
    if (paper.view) {
      paper.view.viewSize = new paper.Size(canvasSize.width, canvasSize.height);
    }
  }, [canvasSize]);

  return {
    canvasRef,
    containerRef,
    canvasSize,
    paperInitializedRef,
  };
};
